// Two-step login (twoFactor.js on the server): the code step after the password, and the
// Settings section to turn it on or off.
import { useEffect, useState } from 'react';
import { api } from './api';
import { useI18n } from './i18n';

// After the password: the 6-digit code from the app, or a backup code.
export function CodeStep({ ticket, onAuthenticated, onRestart }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onAuthenticated(await api('/auth/2fa/verify', { method: 'POST', body: { ticket, code } }));
    } catch (err) {
      if (err.data && err.data.expired) onRestart();
      setError(err.message);
      setBusy(false);
    }
  }
  return (
    <form className="auth-card" onSubmit={submit}>
      <h2>{t('Enter your code')}</h2>
      <p className="hint">{t('Open your authenticator app and type the 6-digit code for Market Matrix.')}</p>
      <label>
        {t('Code')}
        <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" autoFocus required
          placeholder="123 456" maxLength={12} />
      </label>
      <p className="field-hint">{t('Lost your phone? Type one of your backup codes instead (like abcde-12345).')}</p>
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy}>{busy ? t('Please wait…') : t('Log in')}</button>
      <p className="auth-switch"><button type="button" className="link-button" onClick={onRestart}>{t('Start over')}</button></p>
    </form>
  );
}

function BackupCodes({ codes, onDone }) {
  const { t } = useI18n();
  const text = codes.join('\n');
  const [copied, setCopied] = useState(false);
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`Market Matrix backup codes\nEach code works once.\n\n${text}\n`], { type: 'text/plain' }));
    a.download = 'market-matrix-backup-codes.txt';
    a.click();
  };
  return (
    <div className="twofa-backup">
      <p><strong>{t('Save these backup codes somewhere safe.')}</strong> {t('If you lose your phone, each one lets you log in once. They won’t be shown again.')}</p>
      <ul>{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
      <div className="settings-actions">
        <button type="button" className="secondary small" onClick={download}>{t('Download')}</button>
        <button type="button" className="secondary small" onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => {})}>{copied ? t('Copied') : t('Copy')}</button>
        <button type="button" className="small" onClick={onDone}>{t('I saved them')}</button>
      </div>
    </div>
  );
}

// Settings → Two-step login.
export function TwoStepSection({ onSessionExpired }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // { qr, secret }
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState({ code: '', password: '' });
  const [mode, setMode] = useState(''); // '', 'disable', 'regen'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = () => api('/auth/2fa/status').then(setStatus).catch((err) => (err.status === 401 ? onSessionExpired() : setStatus({ enabled: false })));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (status && window.location.hash === '#two-step') document.getElementById('two-step')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [status]);

  async function run(fn) {
    setBusy(true);
    setError('');
    try { await fn(); } catch (err) { if (err.status === 401) return onSessionExpired(); setError(err.message); } finally { setBusy(false); }
  }
  const start = () => run(async () => setSetup(await api('/auth/2fa/setup', { method: 'POST' })));
  const enable = (e) => { e.preventDefault(); run(async () => { const r = await api('/auth/2fa/enable', { method: 'POST', body: { code: form.code } }); setSetup(null); setCodes(r.backupCodes); setForm({ code: '', password: '' }); await load(); }); };
  const disable = (e) => { e.preventDefault(); run(async () => { await api('/auth/2fa/disable', { method: 'POST', body: form }); setMode(''); setForm({ code: '', password: '' }); await load(); }); };
  const regen = (e) => { e.preventDefault(); run(async () => { const r = await api('/auth/2fa/backup-codes', { method: 'POST', body: { code: form.code } }); setCodes(r.backupCodes); setMode(''); setForm({ code: '', password: '' }); await load(); }); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="settings-card" id="two-step">
      <h2>{t('Two-step login')}</h2>
      <p className="settings-text">{t('After your password, Market Matrix also asks for a 6-digit code from an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, 1Password and others). Someone with your password still can’t get in without your phone.')}</p>
      {!status ? <p className="settings-text">{t('Loading…')}</p> : codes ? (
        <BackupCodes codes={codes} onDone={() => setCodes(null)} />
      ) : status.enabled ? (
        <>
          <div className="push-device"><span className="push-dot on" aria-hidden="true" /><strong>{t('Two-step login is on')}</strong>
            <span className="field-hint">{t('{n} backup codes left', { n: status.backupLeft })}</span></div>
          {mode ? (
            <form className="twofa-form" onSubmit={mode === 'disable' ? disable : regen}>
              {mode === 'disable' && <label>{t('Password')}<input type="password" value={form.password} onChange={set('password')} autoComplete="current-password" required /></label>}
              <label>{t('Code from your app')}<input value={form.code} onChange={set('code')} inputMode="numeric" autoComplete="one-time-code" required maxLength={12} /></label>
              <div className="settings-actions">
                <button type="button" className="secondary small" onClick={() => { setMode(''); setError(''); }}>{t('Cancel')}</button>
                <button type="submit" className={`small${mode === 'disable' ? ' danger' : ''}`} disabled={busy}>{mode === 'disable' ? t('Turn off two-step login') : t('Make new backup codes')}</button>
              </div>
            </form>
          ) : (
            <div className="settings-actions">
              <button type="button" className="secondary small" onClick={() => setMode('regen')}>{t('New backup codes')}</button>
              <button type="button" className="secondary small" onClick={() => setMode('disable')}>{t('Turn off')}</button>
            </div>
          )}
        </>
      ) : setup ? (
        <form className="twofa-setup" onSubmit={enable}>
          <ol>
            <li>{t('Open your authenticator app and add a new account (usually a + button).')}</li>
            <li>{t('Scan this QR code, or type the key below.')}</li>
          </ol>
          <img src={setup.qr} alt={t('QR code for your authenticator app')} width="200" height="200" />
          <p className="twofa-key"><span>{t('Key')}</span> <code>{setup.secret}</code></p>
          <label>{t('Type the 6-digit code your app shows')}<input value={form.code} onChange={set('code')} inputMode="numeric" autoComplete="one-time-code" required maxLength={7} autoFocus /></label>
          <div className="settings-actions">
            <button type="button" className="secondary small" onClick={() => setSetup(null)}>{t('Cancel')}</button>
            <button type="submit" className="small" disabled={busy}>{t('Turn on')}</button>
          </div>
        </form>
      ) : (
        <div className="settings-actions"><button type="button" className="small" onClick={start} disabled={busy}>{t('Turn on two-step login')}</button></div>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
